import { groupBy, differenceWith, isEqual, mapValues, uniq, omit, orderBy } from 'lodash'
import { createSelector } from 'reselect'

import { outOfOrderSearch } from '../helpers/outOfOrderSearch'
import { isSeries } from '../helpers/books'

// Other selector dependencies
import { allBookIdsSelector, allBooksSelector } from './booksFirstOrder'
import { allCharactersSelector, singleCharacterSelector } from './charactersFirstOrder'
import {
  attributesSelector,
  characterAttributsForBookByIdSelector,
  characterPositionAttributeIdSelector,
} from './attributesFirstOrder'
import { characterCustomAttributesSelector } from './customAttributesFirstOrder'
import { sortedCharacterCategoriesSelector } from './categoriesFirstOrder'
import { sortEachCategory } from './sortEachCategory'
import {
  charactersSearchTermSelector,
  characterFilterSelector,
  characterCustomAttributeOrderSelector,
  characterSortSelector,
  currentTimelineSelector,
  currentViewSelector,
  isCharactersManuallySortedSelector,
  characterTabSelector,
  selectedCharacterAttributeTabSelector,
  characterAttributesForCurrentBookSelector,
  allNotesInBookSelector,
  showBookTabsSelector,
} from './secondOrder'
import { charactersSortedAtoZSelector } from './charactersFirstOrder'
import { placesSortedAtoZSelector } from './placesFirstOrder'
import { allCardsSelector } from './cardsFirstOrder'
import { createDeepEqualSelector } from './createDeepEqualSelector'
import { characterSearchHits } from './entitySearch'
import { addUncategorisedCategory } from './category'

/**
 * @template A
 * @typedef {import('../index').CategorisedEntities<A>} CategorisedEntities
 */

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').MixedDenormalisedAttribute} MixedDenormalisedAttribute
 * @typedef {import('../index').TextAttributeData} TextAttributeData
 * @typedef {import('../index').CharacterMetadata} CharacterMetadata
 * @typedef {import('../index').Attribute} Attribute
 * @typedef {import('../index').Book} Book
 */

/**
 * @param {Character} character
 * @param {'all' | number} bookId
 * @param {Record<String, Attribute>} currentBookAttributeDescriptorsById
 * @return  {Character}
 */
const displayedSingleCharacter = (character, bookId, currentBookAttributeDescriptorsById) => {
  const currentBookAttributes =
    character.attributes.filter((attribute) => {
      return bookId === attribute.bookId
    }) || []
  /**
   * @type {Array<number>}
   */
  const allCharacterTags = []
  const currentBookTagsAttributeValue = currentBookAttributes.find((attribute) => {
    return (
      attribute.bookId === bookId &&
      currentBookAttributeDescriptorsById[attribute.id]?.type === 'base-attribute' &&
      currentBookAttributeDescriptorsById[attribute.id]?.name === 'tags'
    )
  })?.value
  const characterPerBookTags =
    Array.isArray(currentBookTagsAttributeValue) &&
    currentBookTagsAttributeValue.every((x) => typeof x === 'number')
      ? currentBookTagsAttributeValue
      : bookId === 'all'
        ? character.tags
        : []
  if (characterPerBookTags.length) {
    allCharacterTags.push(...characterPerBookTags)
  }

  if (character.tags) {
    character.tags?.forEach((tag) => {
      if (!allCharacterTags.includes(tag)) {
        allCharacterTags.push(tag)
      }
    })
  }

  const descriptionAttribute = currentBookAttributes.find((attribute) => {
    return (
      attribute.bookId === bookId &&
      currentBookAttributeDescriptorsById[attribute.id]?.type === 'base-attribute' &&
      currentBookAttributeDescriptorsById[attribute.id]?.name === 'shortDescription'
    )
  })
  const description =
    typeof descriptionAttribute?.value === 'string'
      ? descriptionAttribute.value
      : (bookId === 'all' && character.description) || ''

  const notesAttribute = currentBookAttributes.find((attribute) => {
    return (
      attribute.bookId === bookId &&
      currentBookAttributeDescriptorsById[attribute.id]?.type === 'base-attribute' &&
      currentBookAttributeDescriptorsById[attribute.id]?.name === 'description'
    )
  })
  const notes =
    typeof notesAttribute?.value === 'string' ||
    (Array.isArray(notesAttribute?.value) &&
      notesAttribute.value.every((x) => typeof x !== 'number'))
      ? notesAttribute.value
      : (bookId === 'all' && character.notes) || ''

  const category = currentBookAttributes.find((attribute) => {
    return (
      attribute.bookId === bookId &&
      currentBookAttributeDescriptorsById[attribute.id]?.type === 'base-attribute' &&
      currentBookAttributeDescriptorsById[attribute.id]?.name === 'category'
    )
  })
  const categoryId =
    typeof category?.value === 'string'
      ? category?.value
      : (bookId === 'all' && character.categoryId) || null

  return {
    ...character,
    tags: allCharacterTags,
    description,
    notes,
    categoryId,
    name: character.name ?? '',
  }
}

/**
 * @type {function(PlottrState): ('all' | number)}
 */
export const characterAttributeTabSelector = createSelector(
  selectedCharacterAttributeTabSelector,
  showBookTabsSelector,
  (selectedTab, showTabs) => {
    return !showTabs ? 'all' : selectedTab
  }
)

// NOTE(ed): all the selectors for characters that take two extra
// parameters expect a character Id and the id of the book where the
// attributes should be drawn from or null to use the current timeline
// as the book.
//
// i.e. the parameters look the below function:
// overriddenBookIdSelector.

/**
 * @param {PlottrState} _state
 * @param {number} _characterId
 * @param {number | 'all' | null} bookId
 * @return {number | 'all' | Null}
 */
const overriddenBookIdSelector = (_state, _characterId, bookId) => bookId
/**
 * NOTE(ed): the character id could be null in this case because we're
 * re-using the overriddenBookIdSelector and we don't care about
 * parameter #2.
 *
 * @type {function(PlottrState, number | Null, number | 'all' | null): Array<Character>}
 */
export const allDisplayedCharactersSelector = createSelector(
  allCharactersSelector,
  characterAttributeTabSelector,
  characterAttributsForBookByIdSelector,
  overriddenBookIdSelector,
  (characters, bookId, currentBookAttributeDescriptorsById, overridenBookId) => {
    return characters.map((character) =>
      displayedSingleCharacter(
        character,
        overridenBookId || bookId,
        currentBookAttributeDescriptorsById
      )
    )
  }
)

/**
 * @type {function(PlottrState, number, number): Record<String, Array<Character>>}
 */
export const displayedCharactersByCategorySelector = createSelector(
  allDisplayedCharactersSelector,
  (characters) => groupBy(characters, 'categoryId')
)

/**
 * @type {function(PlottrState): boolean}
 */
export const characterFilterIsEmptySelector = createSelector(
  characterFilterSelector,
  characterAttributesForCurrentBookSelector,
  (filter, attributes) => {
    if (!filter) return true
    const allAttributes = [{ name: 'tag' }, { name: 'book' }, { name: 'category' }, ...attributes]
    return !allAttributes.some((attr) => {
      const key = 'id' in attr ? attr.id : attr.name
      return typeof key !== 'undefined' && filter[key] && filter[key].length
    })
  }
)

// TODO: port the other similar selectors to work in the same way as
// this one.  It's waaaaay more efficient(!)
/**
 * @type {function(PlottrState, number | Null, number | 'all'): Array<Character>}
 */
export const allFilteredSearchedCharactersSelector = createSelector(
  allCharactersSelector,
  characterFilterSelector,
  characterFilterIsEmptySelector,
  charactersSearchTermSelector,
  characterAttributeTabSelector,
  overriddenBookIdSelector,
  attributesSelector,
  allBooksSelector,
  characterCustomAttributesSelector,
  (
    characters,
    filter,
    filterIsEmpty,
    searchTerm,
    bookId,
    overridenBookId,
    allAttributes,
    allBooks,
    legacyCustomAttributes
  ) => {
    const bookIdToUse = bookId ?? overridenBookId
    // Eliminate characters who don't match the filter
    const filteredCharacters =
      filterIsEmpty || filter === null
        ? characters
        : characters.filter((character) => {
            return Object.keys(filter).some((attr) => {
              if (attr == 'tag') {
                const tagAttribute = allAttributes.characters.find(({ type, name }) => {
                  return type === 'base-attribute' && name === 'tags'
                })
                /**
                 * @type {Array<number>}
                 */
                const values = filter[attr] ?? []
                return values.some((val) => {
                  if (!tagAttribute) {
                    return character.tags.some((tagId) => tagId === val)
                  } else {
                    const attributeValueForBook = character.attributes.find((attribute) => {
                      return attribute.id === tagAttribute.id && attribute.bookId === bookIdToUse
                    })
                    return (
                      Array.isArray(attributeValueForBook?.value) &&
                      attributeValueForBook.value.some((bId) => bId === val)
                    )
                  }
                })
              } else if (attr == 'category') {
                const categoryAttribute = allAttributes.characters.find(({ type, name }) => {
                  return type === 'base-attribute' && name === 'category'
                })
                /**
                 * @type {Array<number>}
                 */
                const values = filter[attr] ?? []
                return values.some((val) => {
                  if (!categoryAttribute) {
                    return character.categoryId == String(val)
                  } else {
                    const attributeValueForBook = character.attributes.find((attribute) => {
                      return (
                        attribute.id === categoryAttribute.id && attribute.bookId === bookIdToUse
                      )
                    })
                    return attributeValueForBook?.value == val
                  }
                })
              } else if (attr == 'book') {
                /**
                 * @type {Array<number | 'series'>}
                 */
                const values = filter[attr] ?? []
                return values.some((val) => {
                  return character.bookIds.includes(val)
                })
              } else {
                // It could be a new attribute
                const characterAttributes = character.attributes || []
                const attributeFound = characterAttributes.find((attribute) => {
                  return attribute?.id?.toString() === attr && attribute.bookId === bookIdToUse
                })
                /**
                 * @type {Array<String>}
                 */
                const values = filter[attr]
                return values.some((val) => {
                  const definesAttribute =
                    attributeFound?.value === val ||
                    (attributeFound && attributeFound.value === undefined && val === '')
                  if (definesAttribute) {
                    return true
                  }

                  // Or it could be a legacy attribute
                  return (
                    !attributeFound &&
                    ((val === '' && (!character[attr] || character[attr] === '')) ||
                      (val !== '' && character[attr] && character[attr] === val))
                  )
                })
              }
            })
          })
    // Now eliminate all those without search hits
    const allSearchHitsAcrossCharacters = characterSearchHits(
      searchTerm,
      filteredCharacters,
      allAttributes,
      allBooks,
      legacyCustomAttributes,
      false
    )
    return typeof searchTerm === 'string' && searchTerm !== ''
      ? filteredCharacters.filter((character) => {
          return allSearchHitsAcrossCharacters.some(({ path }) => {
            return path.startsWith(`/characters/${character.id}`)
          })
        })
      : filteredCharacters
  }
)

/**
 * @type {function(PlottrState, number | Null, number | 'all' | null): Record<String, Array<Character>>}
 */
export const visibleSortedCharactersByCategorySelector = createSelector(
  allDisplayedCharactersSelector,
  displayedCharactersByCategorySelector,
  characterFilterSelector,
  characterFilterIsEmptySelector,
  characterSortSelector,
  characterAttributesForCurrentBookSelector,
  characterAttributeTabSelector,
  isCharactersManuallySortedSelector,
  characterPositionAttributeIdSelector,
  (
    allCharacters,
    charactersByCategory,
    filter,
    filterIsEmpty,
    sort,
    allAttributes,
    bookId,
    isManuallySorted,
    positionAttributeId
  ) => {
    if (!allCharacters.length) {
      return {}
    } else {
      let visible = charactersByCategory
      if (!filterIsEmpty && filter !== null) {
        visible = {}
        allCharacters.forEach((ch) => {
          const matches = Object.keys(filter).some((attr) => {
            if (attr === 'tag') {
              /**
               * @type {Attribute | Null}
               */
              let tagAttribute = null
              for (const attribute of allAttributes) {
                if (
                  'id' in attribute &&
                  attribute.type === 'base-attribute' &&
                  attribute.name === 'tags'
                ) {
                  tagAttribute = attribute
                }
              }
              /**
               * @type {Array<number>}
               */
              const values = filter[attr] ?? []
              return values.some((val) => {
                if (!tagAttribute) {
                  return ch.tags.some((tagId) => tagId === val)
                } else {
                  const attributeValueForBook = ch.attributes.find((attribute) => {
                    return attribute.id === tagAttribute.id && attribute.bookId === bookId
                  })
                  return (
                    Array.isArray(attributeValueForBook?.value) &&
                    attributeValueForBook.value.some((bId) => bId === val)
                  )
                }
              })
            } else if (attr == 'category') {
              /**
               * @type {Attribute | Null}
               */
              let categoryAttribute = null
              for (const attribute of allAttributes) {
                if (
                  'id' in attribute &&
                  attribute.type === 'base-attribute' &&
                  attribute.name === 'tags'
                ) {
                  categoryAttribute = attribute
                }
              }
              /**
               * @type {Array<number>}
               */
              const values = filter[attr] ?? []
              return values.some((val) => {
                if (!categoryAttribute) {
                  return ch.categoryId == String(val)
                } else {
                  const attributeValueForBook = ch.attributes.find((attribute) => {
                    return attribute.id === categoryAttribute.id && attribute.bookId === bookId
                  })
                  return attributeValueForBook?.value == val
                }
              })
            } else if (attr == 'book') {
              /**
               * @type {Array<number | 'series'>}
               */
              const values = filter[attr] ?? []
              return values.some((val) => {
                return ch.bookIds.includes(val)
              })
            } else {
              // It could be a new attribute
              const characterAttributes = ch.attributes || []
              const attributeFound = characterAttributes.find((attribute) => {
                return attribute?.id?.toString() === attr && attribute.bookId === bookId
              })
              /**
               * @type {Array<String>}
               */
              const values = filter[attr]
              return values.some((val) => {
                const definesAttribute =
                  attributeFound?.value === val ||
                  (attributeFound && attributeFound.value === undefined && val === '')
                if (definesAttribute) {
                  return true
                }

                // Or it could be a legacy attribute
                return (
                  !attributeFound &&
                  ((val === '' && (!ch[attr] || ch[attr] === '')) ||
                    (val !== '' && ch[attr] && ch[attr] === val))
                )
              })
            }
          })
          if (matches) {
            // categoryId might be null.  Use the string "null" in that case.
            if (visible[`${ch.categoryId}`] && visible[`${ch.categoryId}`].length) {
              visible[`${ch.categoryId}`].push(ch)
            } else {
              visible[`${ch.categoryId}`] = [ch]
            }
          }
        })
      } else {
        visible = mapValues(visible, (ch) => {
          return ch.filter((c) => bookId === 'all' || c.bookIds.includes(bookId))
        })
      }
      return sortEachCategory(
        visible,
        sort,
        isManuallySorted,
        positionAttributeId,
        bookId === 'all' ? 'series' : bookId
      )
    }
  }
)

/**
 * @type {function(PlottrState, number | Null, number | 'all' | null): (CategorisedEntities<Character>)}
 */
export const charactersWithSortedCategoryInfoSelector = createSelector(
  visibleSortedCharactersByCategorySelector,
  sortedCharacterCategoriesSelector,
  (charactersByCategory, categories) => {
    return addUncategorisedCategory(categories).map((category) => ({
      ...category,
      items: (charactersByCategory[`${category.id}`] || []).sort((a, b) =>
        a.name.localeCompare(b.name)
      ), // Sort items alphabetically
    }))
  }
)

/**
 * @type {function(PlottrState): Record<number, String>}
 */
const stringifiedCharactersByIdSelector = createSelector(allCharactersSelector, (characters) => {
  return characters.reduce((acc, nextCharacter) => {
    return {
      ...acc,
      [nextCharacter.id]: JSON.stringify(nextCharacter).toLowerCase(),
    }
  }, {})
})

/**
 * @type {function(PlottrState, number | Null, number | 'all' | null): Record<String, Array<Character>>}
 */
export const visibleSortedSearchedCharactersByCategorySelector = createSelector(
  visibleSortedCharactersByCategorySelector,
  charactersSearchTermSelector,
  stringifiedCharactersByIdSelector,
  (characterCategories, searchTerm, stringifiedCharacters) => {
    if (!searchTerm) return characterCategories

    const lowSearchTerms = searchTerm
      .toLowerCase()
      .split(' ')
      .filter((x) => x)
    return Object.entries(characterCategories).reduce((acc, nextCategory) => {
      const [key, characters] = nextCategory
      const newCharacters = characters.filter(({ id }) => {
        return outOfOrderSearch(lowSearchTerms, stringifiedCharacters[id])
      })
      if (newCharacters.length > 0) {
        return {
          ...acc,
          [key]: newCharacters,
        }
      } else {
        return acc
      }
    }, {})
  }
)

/**
 * @param {Character} character
 * @return {CharacterMetadata}
 */
const characterMetadata = (character) => {
  return {
    id: character.id,
    categoryId: character.categoryId,
  }
}
/**
 * @type {function(PlottrState, number | Null, number | 'all' | null): Record<String, Array<CharacterMetadata>>}
 */
const _visibleSortedSearchedCharacterMetadataByCategorySelector = createSelector(
  visibleSortedSearchedCharactersByCategorySelector,
  (categories) => {
    return Object.entries(categories).reduce((acc, nextEntry) => {
      const [key, characters] = nextEntry

      return {
        ...acc,
        [key]: characters.map(characterMetadata),
      }
    }, {})
  }
)
/**
 * @type {function(PlottrState, number | Null, number | 'all' | null): Record<String, Array<CharacterMetadata>>}
 */
export const visibleSortedSearchedCharacterMetadataByCategorySelector = createDeepEqualSelector(
  _visibleSortedSearchedCharacterMetadataByCategorySelector,
  (characterCategoryMetadata) => {
    return characterCategoryMetadata
  }
)

// This selector produces a character with overridden attributes based
// on the book we're looking at.
/**
 * @type {function(PlottrState, 'all' | number): (Character | Null)}
 */
export const displayedSingleCharacterSelector = createSelector(
  singleCharacterSelector,
  characterAttributeTabSelector,
  characterAttributsForBookByIdSelector,
  (character, bookTab, attributes) => {
    if (character) {
      return displayedSingleCharacter(character, bookTab, attributes)
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState, number | Null, number | 'all' | Null): Array<Character>}
 */
export const allDisplayedCharactersForCurrentBookSelector = createSelector(
  allDisplayedCharactersSelector,
  characterAttributeTabSelector,
  overriddenBookIdSelector,
  (characters, selectedBookId, overriddenBookId) => {
    const bookId = overriddenBookId || selectedBookId || 'series'
    if (bookId === 'all' || bookId === 'series') {
      return characters
    }

    return characters.filter((character) => {
      return character.bookIds.indexOf(bookId) > -1
    })
  }
)

/**
 * @param {PlottrState} _state
 * @param {number} characterId
 * @return {number}
 */
const characterIdSelector = (_state, characterId) => characterId
/**
 * @param {PlottrState} _state
 * @param {number} _characterId
 * @param {String} templateId
 * @return {String}
 */
const templateIdSelector = (_state, _characterId, templateId) => templateId
/**
 * @param {PlottrState} _state
 * @param {number} _characterId
 * @param {String} _templateId
 * @param {String} attributeName
 * @return {String}
 */
const attributeNameSelector = (_state, _characterId, _templateId, attributeName) => attributeName
/**
 * @type {function(PlottrState, number, String, String): TextAttributeData}
 */
export const characterTemplateAttributeValueSelector = createSelector(
  characterIdSelector,
  templateIdSelector,
  attributeNameSelector,
  characterAttributeTabSelector,
  singleCharacterSelector,
  (characterId, templateId, attributeName, bookId, character) => {
    const templateOnCharacter = character && character.templates.find(({ id }) => id === templateId)
    const valueInAttributes = templateOnCharacter
      ? templateOnCharacter.attributes.find(({ name }) => name === attributeName)?.value
      : null
    const valueOnTemplate = templateOnCharacter && templateOnCharacter[attributeName]
    const valueForBook = templateOnCharacter?.values?.find?.((value) => {
      return value.name === attributeName && value.bookId === bookId
    })?.value
    return valueForBook || (bookId === 'all' && (valueInAttributes || valueOnTemplate))
  }
)

/**
 * IMPORTANT NOTE: This object is a little too flexible for our
 * limited access to Typescript facilities.  There's a value property
 * for entities categorised in each CategoryEntry.  The value is found
 * on this object at `key`, and will be an array of numbers.  You'll
 * have to typecheck it at runtime to confirm, sorry!
 *
 * @typedef CategoryEntry
 * @property {String} [glyph]
 * @property {boolean} displayHeading
 * @property {String} key
 * @property {boolean} lineAbove
 */

/**
 * @typedef {Array<CategoryEntry>} EntityInBookCategoryDisplay
 */

/**
 * @type {function(PlottrState): EntityInBookCategoryDisplay}
 */
export const characterBookCategoriesSelector = createSelector(
  currentTimelineSelector,
  allCharactersSelector,
  (bookId, characters) => {
    const categoryMembership = characters.reduce((acc, character) => {
      if (character.bookIds.indexOf(bookId) > -1) {
        return {
          ...acc,
          'Characters In Book': [character.id, ...(acc['Characters In Book'] || [])],
        }
      }

      return {
        ...acc,
        'Not in Book': [character.id, ...(acc['Not in Book'] || [])],
      }
    }, {})
    return [
      categoryMembership['Characters In Book']?.length > 0
        ? [
            {
              displayHeading: false,
              key: 'Characters In Book',
              'Characters In Book': categoryMembership['Characters In Book'],
            },
          ]
        : [],
      categoryMembership['Not in Book']?.length > 0
        ? [
            {
              glyph: 'plus',
              displayHeading: true,
              key: 'Not in Book',
              'Not in Book': categoryMembership['Not in Book'],
              lineAbove: true,
            },
          ]
        : [],
    ].flatMap(
      // @ts-ignore
      (x) => {
        // @ts-ignore
        return x
      }
    )
  }
)

const combinedAttributesForCharacter = (
  character,
  attributes,
  customAttributes,
  selectedBookId,
  overridenBookId,
  order
) => {
  const characterBookAttributes = character.attributes || []
  const allAttributes = attributes.characters || []
  const bookId = overridenBookId || selectedBookId
  const newAttributes = allAttributes
    .filter((bookAttribute) => {
      return bookAttribute.type !== 'base-attribute'
    })
    .map((bookAttribute) => {
      const value = characterBookAttributes.find((attributeValue) => {
        return attributeValue.id === bookAttribute.id && attributeValue.bookId === bookId
      })
      return {
        value: '',
        ...bookAttribute,
        ...value,
      }
    })

  const legacyAttributes = customAttributes.map((customAttribute) => {
    const value = character[customAttribute.name] || ''
    return {
      ...customAttribute,
      value,
    }
  })

  const ordered = order.map((entry) => {
    if (entry.type === 'attributes') {
      return newAttributes.find(({ id }) => {
        return id === entry.id
      })
    } else {
      return legacyAttributes.find(({ name }) => {
        return name === entry.name
      })
    }
  })
  const missing = differenceWith([...newAttributes, ...legacyAttributes], ordered, isEqual)

  // filter(Boolean) accounts for transitive states transmitting the
  // characters that have attributes without the file yet having the
  // attribute.  (Adding an attribute from mobile causes one such
  // transitive state.)
  return [...ordered, ...missing].filter(Boolean)
}

/**
 * @type {function(PlottrState, number, number | 'all' | null): Array<MixedDenormalisedAttribute>}
 */
export const characterAttributesSelector = createSelector(
  singleCharacterSelector,
  attributesSelector,
  characterCustomAttributesSelector,
  characterAttributeTabSelector,
  overriddenBookIdSelector,
  characterCustomAttributeOrderSelector,
  combinedAttributesForCharacter
)

/**
 * A mapping from the names or ids of attributes (dependending on
 * whether they're legacy or new -new attributes have integer ids-) to
 * all the values they take on across all characters.
 *
 * @type {function(PlottrState): Record<number | String, Array<TextAttributeData>>}
 */
export const characterAttributeValuesForCurrentBookSelector = createSelector(
  allCharactersSelector,
  attributesSelector,
  characterCustomAttributesSelector,
  characterAttributeTabSelector,
  characterCustomAttributeOrderSelector,
  (characters, attributes, customAttributes, bookId, order) => {
    const newAttributeValues = characters.reduce((acc, character) => {
      const attributesForCharacter = combinedAttributesForCharacter(
        character,
        attributes,
        customAttributes,
        bookId,
        null,
        order
      )
      attributesForCharacter.forEach((attribute) => {
        if (attribute.value !== undefined && attribute.value !== null && attribute.value !== '') {
          if (!Array.isArray(acc[attribute.id])) {
            acc[attribute.id] = [attribute.value]
          }
          acc[attribute.id].push(attribute.value)
          acc[attribute.id] = uniq(acc[attribute.id])
        }
      })
      return acc
    }, {})

    const legacyAttributes = customAttributes.reduce((acc, customAttribute) => {
      const values = characters
        .map((character) => {
          return character[customAttribute.name]
        })
        .filter((value) => {
          return value !== undefined && value !== null && value !== ''
        })
      return {
        ...acc,
        [customAttribute.name]: uniq(values),
      }
    }, {})

    return {
      ...newAttributeValues,
      ...legacyAttributes,
    }
  }
)

/**
 * @type {function(PlottrState): Record<String, Book>}
 */
export const allBooksWithCharactersInThemSelector = createSelector(
  allBooksSelector,
  allCharactersSelector,
  showBookTabsSelector,
  (books, characters, showBookTabs) => {
    if (!showBookTabs) {
      return omit(books, 'allIds')
    }

    return Object.values(books)
      .filter((value) => {
        return (
          !Array.isArray(value) &&
          characters.some((character) => {
            return character.bookIds.indexOf(value.id) !== -1
          })
        )
      })
      .reduce((acc, next) => {
        if (!Array.isArray(next)) {
          return {
            ...acc,
            [next.id]: next,
          }
        } else {
          return acc
        }
      }, {})
  }
)

/**
 * @type {function(PlottrState): Array<Book>}
 */
export const allBooksWithCharactersInThemSortedByPositionInAllBookIdsSelector =
  createDeepEqualSelector(
    allBooksWithCharactersInThemSelector,
    allBookIdsSelector,
    (booksWithCharactersInThem, allIds) => {
      /**
       * @type {Record<number, Book & { position: number }>}
       */
      const booksWithCharactersAnnotatedByPosition = {}

      allIds
        .map((id) => Object.values(booksWithCharactersInThem).find((book) => book.id == id))
        .forEach((item, idx) => {
          if (item) {
            booksWithCharactersAnnotatedByPosition[item.id] = {
              ...item,
              position: idx,
            }
          }
        })

      return orderBy(booksWithCharactersAnnotatedByPosition, 'position')
    }
  )

/**
 * @type {function(PlottrState): Array<Character>}
 */
export const charactersSortedInBookSelector = createSelector(
  charactersSortedAtoZSelector,
  currentTimelineSelector,
  (characters, bookId) =>
    characters.filter((character) => {
      if (character.bookIds.length === 0) return true
      if (character.bookIds.some(isSeries)) return true
      return character.bookIds.includes(bookId)
    })
)

/**
 * @type {function(PlottrState): Array<Character>}
 */
export const charactersFilterItemsSelector = createSelector(
  currentViewSelector,
  charactersSortedAtoZSelector,
  placesSortedAtoZSelector,
  allNotesInBookSelector,
  allCardsSelector,
  (currentView, characters, places, notes, cards) => {
    switch (currentView) {
      case 'places': {
        return []
      }
      case 'notes': {
        const filteredItems = characters.filter((char) =>
          notes.find((note) => note.characters?.includes(char.id))
        )
        return filteredItems
      }
      case 'timeline': {
        const filteredItems = characters.filter((char) =>
          cards.find((card) => card.characters?.includes(char.id))
        )
        return filteredItems
      }
      default:
        return []
    }
  }
)

/**
 * @type {function(PlottrState): Null | number}
 */
export const characterSelectedForChangingImageSelector = createSelector(
  characterTabSelector,
  ({ characterSelectedForChangingImage }) => {
    return characterSelectedForChangingImage
  }
)

/**
 * @type {function(PlottrState): (String | null)}
 */
export const characterSelectedForChangingImagesCurrentImageSelector = createSelector(
  characterSelectedForChangingImageSelector,
  allCharactersSelector,
  (characterId, characters) => {
    return (
      characters.find((character) => {
        return character.id === characterId
      })?.imageId ?? null
    )
  }
)
